import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { useAuth } from "../App";
import { authAxios, logout as authLogout } from "../utils/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  ArrowLeft, Plus, Heart, MessageCircle, Eye, Send, 
  ChevronLeft, ChevronRight, Image as ImageIcon, X, 
  MoreHorizontal, Trash2, Edit, RefreshCw, Reply, Check, AlertTriangle,
  Home
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { useTheme } from "../contexts/ThemeContext";
import NotificationBell from "../components/notifications/NotificationBell";
import AdminMenu from "../components/AdminMenu";
import MainMenu from "../components/MainMenu";
import CoinBalancePill from "../components/CoinBalancePill";

// "17th Sep 2026" style — ordinal day + short month + full year.
const ORDINAL_MONTH = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const _ordinalSuffix = (d) => {
  if (d % 100 >= 11 && d % 100 <= 13) return "th";
  return ({1:"st",2:"nd",3:"rd"})[d % 10] || "th";
};
const fmtNewsDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const day = d.getDate();
  return `${day}${_ordinalSuffix(day)} ${ORDINAL_MONTH[d.getMonth()]} ${d.getFullYear()}`;
};

// ============== IMAGE CAROUSEL COMPONENT ==============
const ImageCarousel = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  if (!images || images.length === 0) return null;
  
  const goLeft = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };
  
  const goRight = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };
  
  return (
    <div className="relative w-full bg-black/20 rounded-lg overflow-hidden group">
      <img
        src={images[currentIndex]}
        alt={`Slide ${currentIndex + 1}`}
        className="block w-full h-auto"
      />
      
      {images.length > 1 && (
        <>
          {/* Navigation Arrows */}
          <button
            onClick={goLeft}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={goRight}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
          
          {/* Dot Indicators */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
            {images.map((_, idx) => (
              <button
                key={idx}
                onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx); }}
                className={`w-2 h-2 rounded-full transition-colors ${
                  idx === currentIndex ? "bg-white" : "bg-white/40 hover:bg-white/60"
                }`}
              />
            ))}
          </div>
          
          {/* Counter */}
          <div className="absolute top-3 right-3 px-2 py-1 bg-black/50 rounded text-white text-xs">
            {currentIndex + 1} / {images.length}
          </div>
        </>
      )}
    </div>
  );
};

// ============== INLINE BUTTON PARSER ==============
const parseInlineButtons = (content) => {
  // Parse (B(LABEL,LINK)B) syntax
  const buttonRegex = /\(B\(([^,]+),([^)]+)\)B\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  
  while ((match = buttonRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: "text", content: content.slice(lastIndex, match.index) });
    }
    parts.push({ type: "button", label: match[1].trim(), link: match[2].trim() });
    lastIndex = match.index + match[0].length;
  }
  
  if (lastIndex < content.length) {
    parts.push({ type: "text", content: content.slice(lastIndex) });
  }
  
  return parts.length > 0 ? parts : [{ type: "text", content }];
};

// ============== POLL COMPONENT ==============
const PollComponent = ({ poll, userVote, onVote, newsId }) => {
  if (!poll) return null;
  
  const totalVotes = poll.total_votes || Object.values(poll.votes || {}).reduce((a, b) => a + (Array.isArray(b) ? b.length : b), 0);
  
  return (
    <div className="bg-white/5 rounded-lg p-4 mt-4">
      <p className="text-white/60 text-sm mb-3">Poll</p>
      <div className="space-y-2">
        {poll.options.map((option) => {
          const voteCount = Array.isArray(poll.votes?.[option]) 
            ? poll.votes[option].length 
            : (poll.votes?.[option] || 0);
          const percentage = totalVotes > 0 ? Math.round((voteCount / totalVotes) * 100) : 0;
          const isSelected = userVote === option;
          
          return (
            <button
              key={option}
              onClick={() => onVote(newsId, option)}
              className={`w-full text-left p-3 rounded-lg transition-colors relative overflow-hidden ${
                isSelected 
                  ? "bg-divine-gold/20 border border-divine-gold/50" 
                  : "bg-white/5 hover:bg-white/10 border border-transparent"
              }`}
            >
              {/* Progress bar background */}
              <div 
                className={`absolute inset-y-0 left-0 transition-all ${
                  isSelected ? "bg-divine-gold/20" : "bg-white/5"
                }`}
                style={{ width: `${percentage}%` }}
              />
              
              <div className="relative flex items-center justify-between">
                <span className={`text-sm ${isSelected ? "text-divine-gold font-medium" : "text-white"}`}>
                  {isSelected && <Check className="w-4 h-4 inline mr-2" />}
                  {option}
                </span>
                <span className="text-white/60 text-sm">{percentage}%</span>
              </div>
            </button>
          );
        })}
      </div>
      <p className="text-white/40 text-xs mt-2">{totalVotes} vote{totalVotes !== 1 ? "s" : ""}</p>
    </div>
  );
};

// ============== COMMENT COMPONENT ==============
const CommentItem = ({ comment, user, onReply, onEdit, onDelete, onLike, depth = 0 }) => {
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(comment.content);
  const [showMenu, setShowMenu] = useState(false);
  
  const isOwner = comment.author_id === user?.user_id;
  const isAdmin = user?.role === "admin" || user?.role === "support";
  const canEdit = isOwner;
  const canDelete = isOwner || isAdmin;
  
  const handleReply = () => {
    if (replyText.trim()) {
      onReply(comment.comment_id, replyText.trim());
      setReplyText("");
      setShowReplyInput(false);
    }
  };
  
  const handleEdit = () => {
    if (editText.trim()) {
      onEdit(comment.comment_id, editText.trim());
      setIsEditing(false);
    }
  };
  
  return (
    <div className={`${depth > 0 ? "ml-8 border-l border-white/10 pl-4" : ""}`}>
      <div className="py-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-divine-gold/20 flex items-center justify-center text-divine-gold text-sm font-medium">
              {comment.author_name?.[0] || "U"}
            </div>
            <div>
              <span className="text-white text-sm font-medium">{comment.author_name}</span>
              <span className="text-white/40 text-xs ml-2">
                {new Date(comment.created_at).toLocaleDateString()}
              </span>
              {comment.updated_at && (
                <span className="text-white/30 text-xs ml-1">(edited)</span>
              )}
            </div>
          </div>
          
          {(canEdit || canDelete) && (
            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-1 text-white/40 hover:text-white rounded"
              >
                <MoreHorizontal className="w-4 h-4" />
              </button>
              {showMenu && (
                <div className="absolute right-0 top-6 bg-divine-charcoal border border-white/10 rounded-lg shadow-lg py-1 z-10">
                  {canEdit && (
                    <button
                      onClick={() => { setIsEditing(true); setShowMenu(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-white/70 hover:bg-white/5 flex items-center gap-2"
                    >
                      <Edit className="w-4 h-4" /> Edit
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => { onDelete(comment.comment_id); setShowMenu(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" /> Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        
        {isEditing ? (
          <div className="mt-2 space-y-2">
            <Textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="!bg-[#1a1a1a] border-white/20 text-white text-sm"
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleEdit} className="bg-divine-gold text-black text-xs">
                Save
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setIsEditing(false)} className="text-white/60 text-xs">
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-white/80 text-sm mt-2 ml-10">{comment.content}</p>
        )}
        
        <div className="flex items-center gap-4 mt-2 ml-10">
          <button
            onClick={() => onLike(comment.comment_id)}
            className={`flex items-center gap-1 text-xs ${
              comment.user_liked ? "text-red-400" : "text-white/40 hover:text-white/60"
            }`}
          >
            <Heart className={`w-3 h-3 ${comment.user_liked ? "fill-current" : ""}`} />
            {comment.likes > 0 && comment.likes}
          </button>
          {depth < 3 && (
            <button
              onClick={() => setShowReplyInput(!showReplyInput)}
              className="flex items-center gap-1 text-xs text-white/40 hover:text-white/60"
            >
              <Reply className="w-3 h-3" /> Reply
            </button>
          )}
        </div>
        
        {showReplyInput && (
          <div className="mt-2 ml-10 flex gap-2">
            <Input
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Write a reply..."
              className="flex-1 !bg-[#1a1a1a] border-white/20 text-white text-sm"
              onKeyPress={(e) => e.key === "Enter" && handleReply()}
            />
            <Button size="sm" onClick={handleReply} className="bg-divine-gold text-black">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
      
      {/* Nested replies */}
      {comment.replies?.length > 0 && (
        <div className="space-y-0">
          {comment.replies.map((reply) => (
            <CommentItem
              key={reply.comment_id}
              comment={reply}
              user={user}
              onReply={onReply}
              onEdit={onEdit}
              onDelete={onDelete}
              onLike={onLike}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ============== NEWS POST CARD ==============
const NewsPostCard = ({ post, user, onLike, onVote, onViewComments, isCyber }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  // Only admins/support see the "Popup" pill — it's an editorial signal, not
  // something regular readers need to know about.
  const isAdmin = user?.role === "admin" || user?.role === "support";
  
  // Parse content for buttons and remove poll syntax for display
  const displayContent = post.content.replace(/\(P\([^)]+\)P\)/g, "").trim();
  const contentParts = parseInlineButtons(displayContent);
  
  const handleButtonClick = (link) => {
    if (link.startsWith("http")) {
      window.open(link, "_blank");
    } else {
      navigate(link);
    }
  };
  
  return (
    <div
      className="glass rounded-xl p-4 sm:p-6 space-y-3 sm:space-y-4"
      data-news-id={post.news_id}
      data-testid={`news-post-${post.news_id}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0 text-sm sm:text-base ${
            isCyber ? "bg-cyan-500/20 text-cyan-400" : "bg-divine-gold/20 text-divine-gold"
          }`}>
            {post.author_picture ? (
              <img
                src={post.author_picture}
                alt={post.author_name || "Author"}
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              post.author_name?.[0] || "A"
            )}
          </div>
          <div className="min-w-0">
            <p className="text-white font-medium text-sm sm:text-base truncate">{post.author_name}</p>
            <p className="text-white/40 text-[11px] sm:text-xs">
              {fmtNewsDate(post.created_at)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 text-white/40 text-xs sm:text-sm flex-shrink-0">
          <Eye className="w-4 h-4" />
          {post.views}
        </div>
      </div>
      
      {/* Title */}
      <div className="flex flex-wrap items-center gap-2">
        <h3 className={`text-lg sm:text-xl font-semibold break-words ${isCyber ? "text-cyan-400" : "text-divine-gold"}`}>
          {post.title}
        </h3>
        {post.force_popup && isAdmin && (
          <span className="px-2 py-0.5 text-[10px] sm:text-xs font-medium bg-amber-500/20 text-amber-400 rounded-full flex items-center gap-1" data-testid="force-popup-badge">
            <AlertTriangle className="w-3 h-3" />
            Popup
          </span>
        )}
      </div>
      
      {/* Images Carousel */}
      {post.images?.length > 0 && (
        <ImageCarousel images={post.images} />
      )}
      
      {/* Content */}
      <div className="text-white/80 prose prose-invert prose-sm max-w-none break-words">
        {contentParts.map((part, idx) => {
          if (part.type === "button") {
            return (
              <Button
                key={idx}
                onClick={() => handleButtonClick(part.link)}
                className={`mx-1 my-1 ${
                  isCyber 
                    ? "bg-cyan-500 hover:bg-cyan-600 text-black" 
                    : "bg-divine-gold hover:bg-divine-gold/90 text-black"
                }`}
                size="sm"
              >
                {part.label}
              </Button>
            );
          }
          return (
            <div key={idx} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(part.content, {
              // News post bodies embed a <style> block for card CSS. DOMPurify
              // strips <style> by default (XSS defensive), which is why the
              // .ci-card/.ci-outer classes rendered as unstyled divs. Allow
              // it here — content is admin-authored, not user-submitted.
              ADD_TAGS: ["style"],
              ADD_ATTR: ["target"],
            }) }} />
          );
        })}
      </div>
      
      {/* Poll */}
      {post.poll && (
        <PollComponent
          poll={post.poll}
          userVote={post.user_poll_vote}
          onVote={onVote}
          newsId={post.news_id}
        />
      )}
      
      {/* Actions */}
      <div className="flex items-center gap-4 sm:gap-6 pt-3 sm:pt-4 border-t border-white/10">
        <button
          onClick={() => onLike(post.news_id)}
          className={`flex items-center gap-2 transition-colors text-sm sm:text-base ${
            post.user_liked 
              ? "text-red-400" 
              : "text-white/60 hover:text-red-400"
          }`}
        >
          <Heart className={`w-5 h-5 ${post.user_liked ? "fill-current" : ""}`} />
          <span>{post.likes}</span>
        </button>
        <button
          onClick={() => onViewComments(post)}
          className="flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm sm:text-base"
        >
          <MessageCircle className="w-5 h-5" />
          <span>{post.comment_count}</span>
        </button>
      </div>
    </div>
  );
};

// ============== MAIN NEWS PAGE ==============
const News = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  const isCyber = theme === "agent_eros_1";
  const isAdmin = user?.role === "admin" || user?.role === "support";
  
  // State
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [editingPost, setEditingPost] = useState(null);
  
  // Create/Edit form state
  const [formTitle, setFormTitle] = useState("");
  const [formContent, setFormContent] = useState("");
  const [formImages, setFormImages] = useState([]);
  const [formForcePopup, setFormForcePopup] = useState(false);
  // ISO 8601 with local offset — admin can backdate or forward-date a post.
  // "" means "use server time on create". Populated with the existing
  // created_at when editing.
  const [formCreatedAt, setFormCreatedAt] = useState("");
  const [submitting, setSubmitting] = useState(false);
  
  const fileInputRef = useRef(null);

  // Fetch posts
  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await authAxios.get("/news");
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error("Error fetching news:", error);
      toast.error("Failed to load news");
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchPosts();
  }, []);

  // Deep-link handler: when arriving from a bell notification with
  // `?post=<news_id>&comment=<comment_id>`, scroll to the target post and
  // open its comments modal automatically. Runs once posts have loaded.
  useEffect(() => {
    if (loading || posts.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const targetPostId = params.get("post");
    const targetCommentId = params.get("comment");
    if (!targetPostId) return;
    const target = posts.find((p) => p.news_id === targetPostId);
    if (!target) return;

    // Scroll to the post card
    const el = document.querySelector(`[data-news-id="${targetPostId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    // If the notification was about a comment, open the comments modal too
    if (targetCommentId) {
      handleViewComments(target);
    }

    // Clean the URL so refreshes don't re-trigger the modal
    window.history.replaceState(
      {},
      document.title,
      window.location.pathname
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, posts.length]);
  
  // Like post
  const handleLike = async (newsId) => {
    try {
      const response = await authAxios.post(`/news/${newsId}/like`);
      setPosts(prev => prev.map(p => 
        p.news_id === newsId 
          ? { ...p, likes: response.data.likes, user_liked: response.data.liked }
          : p
      ));
    } catch (error) {
      console.error("Error liking post:", error);
    }
  };
  
  // Vote on poll
  const handleVote = async (newsId, option) => {
    try {
      const response = await authAxios.post(`/news/${newsId}/poll/${encodeURIComponent(option)}`);
      setPosts(prev => prev.map(p => 
        p.news_id === newsId 
          ? { ...p, poll: response.data.poll, user_poll_vote: response.data.voted_option }
          : p
      ));
    } catch (error) {
      console.error("Error voting:", error);
      toast.error("Failed to vote");
    }
  };
  
  // View comments
  const handleViewComments = async (post) => {
    setSelectedPost(post);
    setShowCommentsModal(true);
    
    // Increment view count
    try {
      await authAxios.post(`/news/${post.news_id}/view`);
    } catch (e) { console.error("View count increment failed:", e); }
    
    // Fetch comments
    try {
      const response = await authAxios.get(`/news/${post.news_id}/comments`);
      setComments(response.data.comments || []);
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  };
  
  // Add comment
  const handleAddComment = async (parentId = null, content = null) => {
    const commentContent = content || newComment;
    if (!commentContent.trim() || !selectedPost) return;
    
    try {
      const response = await authAxios.post(`/news/${selectedPost.news_id}/comments`, {
        content: commentContent.trim(),
        parent_id: parentId
      });
      
      if (parentId) {
        // Refresh comments to get proper nesting
        const commentsResponse = await authAxios.get(`/news/${selectedPost.news_id}/comments`);
        setComments(commentsResponse.data.comments || []);
      } else {
        setComments(prev => [...prev, response.data.comment]);
        setNewComment("");
      }
      
      // Update comment count in posts
      setPosts(prev => prev.map(p => 
        p.news_id === selectedPost.news_id 
          ? { ...p, comment_count: p.comment_count + 1 }
          : p
      ));
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Failed to add comment");
    }
  };
  
  // Edit comment
  const handleEditComment = async (commentId, content) => {
    try {
      await authAxios.patch(`/news/comments/${commentId}`, { content });
      const response = await authAxios.get(`/news/${selectedPost.news_id}/comments`);
      setComments(response.data.comments || []);
      toast.success("Comment updated");
    } catch (error) {
      console.error("Error editing comment:", error);
      toast.error("Failed to edit comment");
    }
  };
  
  // Delete comment
  const handleDeleteComment = async (commentId) => {
    try {
      await authAxios.delete(`/news/comments/${commentId}`);
      const response = await authAxios.get(`/news/${selectedPost.news_id}/comments`);
      setComments(response.data.comments || []);
      toast.success("Comment deleted");
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast.error("Failed to delete comment");
    }
  };
  
  // Like comment
  const handleLikeComment = async (commentId) => {
    try {
      await authAxios.post(`/news/comments/${commentId}/like`);
      const response = await authAxios.get(`/news/${selectedPost.news_id}/comments`);
      setComments(response.data.comments || []);
    } catch (error) {
      console.error("Error liking comment:", error);
    }
  };
  
  // Image upload
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const maxFiles = 10 - formImages.length;
    
    files.slice(0, maxFiles).forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 10MB)`);
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormImages(prev => [...prev, e.target.result]);
      };
      reader.readAsDataURL(file);
    });
    
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  
  const removeImage = (index) => {
    setFormImages(prev => prev.filter((_, i) => i !== index));
  };
  
  // Create/Edit post
  const handleSubmitPost = async () => {
    if (!formTitle.trim() || !formContent.trim()) {
      toast.error("Title and content are required");
      return;
    }
    
    setSubmitting(true);
    try {
      // datetime-local produces "YYYY-MM-DDTHH:MM" without a timezone.
      // Convert to full ISO so backend `datetime.fromisoformat` sees it as
      // local time (server converts back to UTC on read).
      const createdAtIso = formCreatedAt
        ? new Date(formCreatedAt).toISOString()
        : null;
      if (editingPost) {
        await authAxios.patch(`/news/${editingPost.news_id}`, {
          title: formTitle,
          content: formContent,
          images: formImages,
          force_popup: formForcePopup,
          ...(createdAtIso ? { created_at: createdAtIso } : {}),
        });
        toast.success("Post updated");
      } else {
        await authAxios.post("/news", {
          title: formTitle,
          content: formContent,
          images: formImages,
          force_popup: formForcePopup,
          ...(createdAtIso ? { created_at: createdAtIso } : {}),
        });
        toast.success("Post created");
      }
      
      setShowCreateModal(false);
      setEditingPost(null);
      setFormTitle("");
      setFormContent("");
      setFormImages([]);
      setFormForcePopup(false);
      setFormCreatedAt("");
      fetchPosts();
    } catch (error) {
      console.error("Error submitting post:", error);
      toast.error(error.response?.data?.detail || "Failed to submit post");
    } finally {
      setSubmitting(false);
    }
  };
  
  // Edit post
  const handleEditPost = (post) => {
    setEditingPost(post);
    setFormTitle(post.title);
    setFormContent(post.content);
    setFormImages(post.images || []);
    setFormForcePopup(post.force_popup || false);
    // datetime-local expects "YYYY-MM-DDTHH:MM" in local time.
    if (post.created_at) {
      const d = new Date(post.created_at);
      const pad = (n) => String(n).padStart(2, "0");
      setFormCreatedAt(
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
      );
    } else {
      setFormCreatedAt("");
    }
    setShowCreateModal(true);
  };
  
  // Delete post
  const handleDeletePost = async (newsId) => {
    if (!confirm("Are you sure you want to delete this post?")) return;
    
    try {
      await authAxios.delete(`/news/${newsId}`);
      toast.success("Post deleted");
      fetchPosts();
    } catch (error) {
      console.error("Error deleting post:", error);
      toast.error("Failed to delete post");
    }
  };
  
  return (
    <div className="min-h-screen min-h-dvh bg-divine-black text-white">

      {/*
        Mobile header sticky fix:
        - `sticky top-0` lives on a plain wrapper div so the `<header>` itself
          (which has `backdrop-blur-sm`, i.e. `backdrop-filter: blur(4px)`)
          isn't both the sticky element *and* a backdrop-filter root. On
          Chrome Android, combining `position: sticky` with `backdrop-filter`
          on the same element can cause the element to be positioned relative
          to the nearest backdrop-filter ancestor / its own containing block
          rather than the viewport, producing a ~50–70px drop the moment
          page content height changes (e.g. when posts finish loading and
          the URL bar collapses).
        - The wrapper carries `top-0 z-50`; the `<header>` keeps its own
          visual chrome (border, glass background, blur).
      */}
      <div className="sticky top-0 z-50">
      <header className="border-b border-white/10 bg-divine-black-light">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                className="text-white hover:text-divine-gold flex-shrink-0"
                data-testid="news-home-btn"
                title="Home"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_c48e0dfd-d860-4af9-851f-221b53f1d8ad/artifacts/h0pj5g64_Agent-Eros-Logo-1.png"
                  alt="Agent Eros"
                  className="w-6 h-6 object-contain"
                  style={{ animation: isCyber ? "eros-glow-cyan 3s ease-in-out infinite" : "eros-glow-gold 3s ease-in-out infinite" }}
                />
              </Button>
              <div className="min-w-0">
                <h1 className={`text-lg truncate ${isCyber ? "font-semibold text-white" : "font-cinzel text-white"}`}>
                  News & Updates
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              <CoinBalancePill />
              <NotificationBell />
              {isAdmin && (
                <Button
                  onClick={() => {
                    setEditingPost(null);
                    setFormTitle("");
                    setFormContent("");
                    setFormImages([]);
                    setFormForcePopup(false);
                    setShowCreateModal(true);
                  }}
                  className={`px-2 sm:px-4 ${isCyber 
                    ? "bg-cyan-500 hover:bg-cyan-600 text-black" 
                    : "bg-divine-gold hover:bg-divine-gold/90 text-black"
                  }`}
                  data-testid="create-news-btn"
                >
                  <Plus className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">NEWS</span>
                </Button>
              )}
              <AdminMenu />
              <MainMenu exclude="/news" />
            </div>
          </div>
        </div>
      </header>
      </div>
      
      {/* Content */}
      <main className="container mx-auto px-3 sm:px-4 pt-5 sm:pt-8 pb-4 sm:pb-8 max-w-2xl">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className={`w-8 h-8 animate-spin ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12">
            <MessageCircle className="w-16 h-16 mx-auto mb-4 text-white/20" />
            <p className="text-white/50">No news posts yet</p>
            {isAdmin && (
              <Button
                onClick={() => setShowCreateModal(true)}
                className="mt-4"
                variant="outline"
              >
                Create First Post
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {posts.map((post) => (
              <div key={post.news_id} className="relative">
                <NewsPostCard
                  post={post}
                  user={user}
                  onLike={handleLike}
                  onVote={handleVote}
                  onViewComments={handleViewComments}
                  isCyber={isCyber}
                />
                {isAdmin && (
                  <div className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 flex gap-1">
                    <button
                      onClick={() => handleEditPost(post)}
                      className="p-2 text-white/50 hover:text-white bg-black/40 hover:bg-black/60 rounded-lg border border-white/10 backdrop-blur-sm"
                      data-testid={`news-post-edit-${post.news_id}`}
                      aria-label="Edit post"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeletePost(post.news_id)}
                      className="p-2 text-white/50 hover:text-red-400 bg-black/40 hover:bg-black/60 rounded-lg border border-white/10 backdrop-blur-sm"
                      data-testid={`news-post-delete-${post.news_id}`}
                      aria-label="Delete post"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
      
      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="w-[calc(100vw-1rem)] sm:w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-divine-black-light border-divine-gold/30 p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className={isCyber ? "text-cyan-400" : "text-divine-gold"}>
              {editingPost ? "Edit News Post" : "Create News Post"}
            </DialogTitle>
            <DialogDescription className="text-white/60">
              Use HTML for formatting. Inline buttons: (B(Label,/link)B) | Polls: (P(Opt1,Opt2,Opt3)P)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-white/80">Title</Label>
              <Input
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="Post title..."
                className="mt-1 !bg-[#1a1a1a] border-white/20 text-white"
              />
            </div>
            
            <div>
              <Label className="text-white/80">Content (HTML)</Label>
              <Textarea
                value={formContent}
                onChange={(e) => setFormContent(e.target.value)}
                placeholder="Write your post content using HTML...

Use <b>bold</b> and <i>italic</i> for emphasis.
Use <a href='/profile'>links</a> for navigation.

Add a button: (B(Click Here,/profile)B)

Add a poll: (P(Yes, No, Maybe)P)"
                className="mt-1 !bg-[#1a1a1a] border-white/20 text-white min-h-[200px] font-mono text-sm"
              />
            </div>
            
            {/* Force Popup Checkbox */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="force-popup"
                checked={formForcePopup}
                onChange={(e) => setFormForcePopup(e.target.checked)}
                className="w-4 h-4 rounded border-white/20 bg-[#1a1a1a] accent-divine-gold cursor-pointer"
                data-testid="force-popup-checkbox"
              />
              <Label htmlFor="force-popup" className="text-white/80 cursor-pointer flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
                Force Popup — show this post as a modal on Dashboard for all users
              </Label>
            </div>

            {/* Post Date (admin can backdate or forward-date) */}
            <div>
              <Label htmlFor="post-date" className="text-white/80">
                Post Date <span className="text-white/40 text-xs">(optional — leave blank to use current time on create)</span>
              </Label>
              <input
                type="datetime-local"
                id="post-date"
                value={formCreatedAt}
                onChange={(e) => setFormCreatedAt(e.target.value)}
                data-testid="post-date-input"
                className="mt-1 w-full px-3 py-2 rounded-md bg-[#1a1a1a] border border-white/10 text-white focus:border-divine-gold focus:outline-none"
              />
            </div>
            
            <div>
              <Label className="text-white/80">Images ({formImages.length}/10)</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageSelect}
                className="hidden"
              />
              
              {formImages.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2 mb-2">
                  {formImages.map((img, idx) => (
                    <div key={idx} className="relative w-20 h-20">
                      <img
                        src={img}
                        alt={`Upload ${idx + 1}`}
                        className="w-full h-full object-cover rounded"
                      />
                      <button
                        onClick={() => removeImage(idx)}
                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {formImages.length < 10 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-2 border-white/20 text-white/70"
                >
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Add Images
                </Button>
              )}
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="border-white/20 text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitPost}
                disabled={submitting}
                className={isCyber 
                  ? "bg-cyan-500 hover:bg-cyan-600 text-black" 
                  : "bg-divine-gold hover:bg-divine-gold/90 text-black"
                }
              >
                {submitting ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : editingPost ? (
                  "Update Post"
                ) : (
                  "Publish Post"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Comments Modal */}
      <Dialog open={showCommentsModal} onOpenChange={setShowCommentsModal}>
        <DialogContent className="w-[calc(100vw-1rem)] sm:w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-divine-black-light border-divine-gold/30 p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className={isCyber ? "text-cyan-400" : "text-divine-gold"}>
              {selectedPost?.title}
            </DialogTitle>
            <DialogDescription className="text-white/60">
              {selectedPost?.comment_count || 0} comments
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4">
            {/* Add comment */}
            <div className="flex gap-2 mb-4">
              <Input
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Write a comment..."
                className="flex-1 !bg-[#1a1a1a] border-white/20 text-white"
                onKeyPress={(e) => e.key === "Enter" && handleAddComment()}
              />
              <Button
                onClick={() => handleAddComment()}
                disabled={!newComment.trim()}
                className={isCyber 
                  ? "bg-cyan-500 hover:bg-cyan-600 text-black" 
                  : "bg-divine-gold hover:bg-divine-gold/90 text-black"
                }
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Comments list */}
            <div className="space-y-0 divide-y divide-white/10">
              {comments.length === 0 ? (
                <p className="text-white/50 text-center py-8">No comments yet. Be the first!</p>
              ) : (
                comments.map((comment) => (
                  <CommentItem
                    key={comment.comment_id}
                    comment={comment}
                    user={user}
                    onReply={handleAddComment}
                    onEdit={handleEditComment}
                    onDelete={handleDeleteComment}
                    onLike={handleLikeComment}
                  />
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Toaster placed at the end so its inline container can't add any
          top-of-viewport spacing above the sticky header (observed on
          mobile). It stays fixed-positioned regardless of placement. */}
      <Toaster richColors position="top-right" />
    </div>
  );
};

export default News;
